import React, { useState } from 'react';

import { Button, Headline } from '@cfm-media/pwa-ui-components';
import { parseHtml } from '../core/utils';

import { HeadlessModal } from './headless-modal';
import { useTranslation } from '../core/utils/translations';
import { ActivityCarousel } from './activity-carousel';
import { useNavigate } from 'react-router-dom';
import { urls } from '../urls';
import { Shuffle } from 'react-feather';

/**
 * @param {Object} props - Die Props für das Modal.
 * @param {string} props.id - ExcursionID.
 * @param {string} props.pickup - Die Abholung.
 * @param {string} props.importantInformation - Wichtige Informationen.
 * @param {string} props.labels - Die Labels.
 * @param {string} props.adultPrice - Preis für Erwachsene.
 * @param {number} props.childMaxAge - Maximales Alter für Kinder.
 * @param {string} props.childPrice - Preis für Kinder.
 * @param {number} props.infantMaxAge - Maximales Alter für Kleinkinder.
 * @param {string} props.infantPrice - Preis für Kleinkinder.
 * @param {function} props.close - Die Funktion zum Schließen des Modals.
 * @param {string} props.dateFrom - Startdatum.
 * @param {string} props.dateTo - Enddatum.
 * @param {string} props.description - Beschreibung.
 * @param {string} props.headline - Überschrift.
 * @param {string[]} props.image - Bild.
 * @param {boolean} props.isOpen - Gibt an, ob das Modal geöffnet ist.
 * @param {string} props.duration - Dauer.
 * @returns {JSX.Element} - Das Modal-Element.
 */

export function ActivityTeaser({
  id,
  adultPrice,
  childMaxAge,
  childPrice,
  dateFrom,
  dateTo,
  description,
  duration,
  title,
  image,
  importantInformation,
  infantMaxAge,
  infantPrice,
  labels,
  pickup,
  onClick,
  weekdays,
  flexibility,
  inclusives,
  highlights,
  ...props
}) {
  const { t } = useTranslation('activities-page');
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const gotToDetailPage = () => {
    navigate(`${urls.excursions.index}/${id}`);
  };

  return (
    <>
      <div
        {...props}
        className="flex flex-col sm:h-[166px] w-full  cursor-pointer overflow-hidden rounded shadow-md sm:flex-row mb-5"
      >
        <div className="w-full sm:w-1/3">
          <img className="h-full w-full object-cover" src={image[0]?.thumb} />
        </div>
        <div className="flex sm:w-2/3 flex-col justify-between rounded-r border-t border-gray-100 p-3 bg-white">
          <div className="flex flex-col sm:flex-row sm:justify-between h-full">
            <div className="h-full">
              <h2 className="font-bold mb-2">{title}</h2>
              <div className="text-sm">{labels.join(', ')}</div>
              <div className="font-semibold text-sm my-1">
                {`${t('activity-info-duration')}: `}
                <span className="font-medium">{duration}</span>
              </div>

              <button
                onClick={setIsOpen.bind(null, true)}
                className="text-gray-400 underline hover:no-underline my-2 text-sm"
              >
                {t('activity-more-info-text')}
              </button>
            </div>
            <div>
              <div className="w-full text-end sm:flex sm:flex-col sm:items-end sm:justify-end h-full">
                {adultPrice ? (
                  <div className="text-start mb-5 sm:text-end">
                    <div className="text-sm">
                      {t('activity-price-from-text')}
                      <span className="text-xl font-semibold">
                        {`${adultPrice}Є`}
                      </span>
                    </div>
                    <div className="text-sm">
                      {t('activity-pro-person-text')}
                    </div>
                  </div>
                ) : null}

                <Button
                  onButtonClick={gotToDetailPage}
                  size="sm"
                  title="Jetzt buchen "
                  variant="primary"
                  className="rounded-lg w-full sm:w-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {isOpen ? (
        <Modal
          id={id}
          action={onClick}
          adultPrice={adultPrice}
          childMaxAge={childMaxAge}
          childPrice={childPrice}
          dateFrom={dateFrom}
          dateTo={dateTo}
          description={description}
          duration={duration}
          title={title}
          image={image}
          importantInformation={importantInformation}
          infantMaxAge={infantMaxAge}
          infantPrice={infantPrice}
          isOpen={isOpen}
          labels={labels}
          pickup={pickup}
          weekdays={weekdays}
          close={setIsOpen.bind(null, false)}
          flexibility={flexibility}
          inclusives={inclusives}
          highlights={highlights}
        />
      ) : null}
    </>
  );
}

export function Modal({
  id,
  action,
  adultPrice,
  childMaxAge,
  childPrice,
  close,
  dateFrom,
  dateTo,
  description,
  duration,
  title,
  image,
  importantInformation,
  infantMaxAge,
  infantPrice,
  isOpen,
  labels,
  pickup,
  weekdays,
  flexibility,
  inclusives,
  highlights,
}) {
  const daysOfWeek = {
    Montag: 'monday',
    Dienstag: 'tuesday',
    Mittwoch: 'wednesday',
    Donnerstag: 'thursday',
    Freitag: 'friday',
    Samstag: 'saturday',
    Sonntag: 'sunday',
  };
  const { t } = useTranslation('activities-page');

  const navigate = useNavigate();

  const gotToDetailPage = () => {
    navigate(`${urls.excursions.index}/${id}`);
  };

  function setImages(images) {
    const slides = [];
    images.forEach((image) => {
      slides.push(image?.thumb);
    });
    return slides;
  }

  const slides = setImages(image);

  return (
    <HeadlessModal isOpen={isOpen} close={close}>
      <>
        {image.length != 0 ? (
          <div className="relative overflow-hidden">
            <ActivityCarousel slides={slides} />
          </div>
        ) : null}

        <div className="p-4">
          {title ? (
            <Headline className="mt-3 mb-2 font-semibold" variant="primary">
              {title}
            </Headline>
          ) : null}

          {labels.length ? (
            <div className="mb-3">
              {labels.map((label, index) => (
                <div key={index} className="mb-2 mr-2 inline-block">
                  <div className="flex cursor-pointer items-center rounded-3xl px-3 py-2 shadow bg-[#e3f0f0] text-brand-1">
                    <span className="text-[10px] font-semibold uppercase">
                      {label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {highlights ? (
            <div className="bg-gray-200 p-3 rounded-md mb-5">
              <span className="text-sm">{highlights}</span>
            </div>
          ) : null}

          <div className="flex items-center border-b-2 border-brand-1 pb-1 mb-3 text-brand-1">
            <span className="icon icon-payment text-xl pr-3" />
            <span className="text-lg font-semibold">
              {t('activity-prices-text')}
            </span>
          </div>
          {adultPrice ? (
            <div className="mb-1 text-sm">
              <span className="pr-1 font-semibold">
                {`${t('activity-label-adult-price')}:`}
              </span>
              <span>{`${adultPrice}Є`}</span>
            </div>
          ) : (
            <div className="mb-1 text-sm">
              <span className="pr-1 font-semibold">
                {`${t('activity-label-adult-price')}:`}
              </span>
              <span>{`${t('activity-not-available')}`}</span>
            </div>
          )}
          {childPrice !== null ? (
            <div className="mb-1 text-sm">
              <span className="pr-1 font-semibold">
                {`${t('activity-label-child-price')}`} {childMaxAge}:
              </span>
              {childPrice === 0 ? (
                <span>{`${t('activity-free-of-charge')}`}</span>
              ) : (
                <span>{`${childPrice}Є`}</span>
              )}
            </div>
          ) : (
            <div className="mb-1 text-sm">
              <span className="pr-1 font-semibold">
                {`${t('activity-label-child-price')}`} {childMaxAge}:
              </span>
              <span>{`${t('activity-not-available')}`}</span>
            </div>
          )}
          {infantPrice !== null ? (
            <div className="mb-1 text-sm">
              <span className="pr-1 font-semibold">
                {`${t('activity-info-infant-price')}`} {infantMaxAge}:
              </span>
              {infantPrice === 0 ? (
                <span>{`${t('activity-free-of-charge')}`}</span>
              ) : (
                <span>{`${infantPrice}Є`}</span>
              )}
            </div>
          ) : (
            <div className="mb-1 text-sm">
              <span className="pr-1 font-semibold">
                {`${t('activity-info-infant-price')}`} {infantMaxAge}:
              </span>
              <span>{`${t('activity-not-available')}`}</span>
            </div>
          )}
          {pickup ? (
            <>
              <div className="flex items-center border-b-2 border-brand-1 pb-1 mb-3 text-brand-1 mt-5">
                <span className="icon icon-transfer text-xl pr-3" />
                <span className="text-lg font-semibold">
                  {t('activity-info-pickup')}
                </span>
              </div>

              <span className="text-sm">{pickup}</span>
            </>
          ) : null}
          {duration ? (
            <>
              <div className="flex items-center border-b-2 border-brand-1 pb-1 mb-3 text-brand-1 mt-5">
                <span className="icon icon-clock text-xl pr-3" />
                <span className="text-lg font-semibold">
                  {t('activity-info-duration')}
                </span>
              </div>

              <span className="text-sm">{duration}</span>
            </>
          ) : null}
          {flexibility ? (
            <>
              <div className="flex items-center border-b-2 border-brand-1 pb-1 mb-3 text-brand-1 mt-5">
                <Shuffle color="#881467" size={20} />
                <span className="text-lg font-semibold pl-3">
                  {t('activity-info-flexibility')}
                </span>
              </div>

              <span className="text-sm">{flexibility}</span>
            </>
          ) : null}
          {description ? (
            <>
              <div className="flex items-center border-b-2 border-brand-1 pb-1 mb-3 text-brand-1 mt-5">
                <span className="icon icon-bookings text-xl pr-3" />
                <span className="text-lg font-semibold">
                  {t('activity-info-description')}
                </span>
              </div>
              <div className="text-sm">{parseHtml(description)}</div>
            </>
          ) : null}
          {inclusives ? (
            <>
              <div className="flex items-center border-b-2 border-brand-1 pb-1 mb-3 text-brand-1 mt-5">
                <span className="icon icon-premium text-xl pr-3" />
                <span className="text-lg font-semibold">
                  {t('activity-info-inclusives')}
                </span>
              </div>

              <span className="text-sm">{parseHtml(inclusives)}</span>
            </>
          ) : null}
          {importantInformation ? (
            <>
              {' '}
              <div className="flex items-center border-b-2 border-brand-1 pb-1 mb-3 text-brand-1 mt-5">
                <span className="icon icon-info text-xl pr-3" />
                <span className="text-lg font-semibold">
                  {t('activity-info-important')}
                </span>
              </div>
              <span className="text-sm">{parseHtml(importantInformation)}</span>
            </>
          ) : null}
          <div className="flex items-center border-b-2 border-brand-1 pb-1 mb-3 text-brand-1 mt-5">
            <span className="icon icon-calendar text-xl pr-3" />
            <span className="text-lg font-semibold">{`${t(
              'activity-info-opening-hours',
            )}`}</span>
          </div>
          {dateFrom || dateTo ? (
            <div className="text-sm mb-2">
              <span className="pr-1 font-semibold">
                {t('activity-info-date')}:
              </span>
              <span className="text-sm">{`${dateFrom} - ${dateTo}`}</span>
            </div>
          ) : null}
          <div className="flex flex-col text-sm">
            {Object.entries(daysOfWeek).map(([label, day], index) => (
              <div className="flex items-center mb-1" key={index}>
                <span className="font-semibold pr-1 w-24">{label}:</span>
                {weekdays[day] ? (
                  <span className="icon icon-check text-xs text-brand-2">
                    {' '}
                  </span>
                ) : (
                  <span className="icon icon-close text-red-600"> </span>
                )}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mt-5">
            <Button
              onButtonClick={gotToDetailPage}
              className="w-full sm:w-auto"
              title="Jetzt buchen"
              variant="primary"
            />
            <Button
              onButtonClick={close}
              className="w-full sm:w-auto"
              title="Schließen"
              variant="primary"
            />
          </div>
        </div>
      </>
    </HeadlessModal>
  );
}
